import { FormTemplate } from 'data/template/template.types';
import { CollectionFunction, CollectionFunctionFormData } from 'data/collectionFunction/collectionFunction.types';
import { CollectionFunctionGroup } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { SupportedFeaturesCollectionFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { Contact } from 'data/contact/contact.types';
import { CfFtpInfo } from 'data/cfFtpInfo/cfFtpInfo.types';
import { Network } from 'data/network/network.types';
import { SecurityInfo } from 'data/securityInfo/securityInfo.types';
import { FqdnGroup } from 'data/fqdnGroup/fqdnGroup.types';
import { NodeData } from 'data/nodes/nodes.types';
import { ValueLabelPair } from 'data/types';
import { EWarrant } from 'data/eWarrant/eWarrant.types';
import {
  adaptTemplate5GFields,
  adaptTemplateOptions,
  adaptTemplateSecinfoidFields,
  adaptTemplatesBasedOnAppSettings,
  adaptTemplateVisibilityMode,
} from 'routes/CollectionFunction/utils/collectionFunctionTemplate.adapter';
import {
  createCollectionFunctionBasedOnTemplate,
} from 'routes/CollectionFunction/utils/collectionFunctionTemplate.factory';
import getCollectionFunctionDataToSubmit from 'routes/CollectionFunction/utils/getCollectionFunctionDataToSubmit';

/**
 * The collection function template every ETSI TS 102 232 part is delivered by, and the
 * template a service that names no handover format falls back to — every part of the
 * standard is served by this one type, so it is the only template such a service could
 * have meant. Kept in sync by hand with EWarrantHandoverFormatMapper.DEFAULT_TAG in
 * xdiscoveryapi.
 */
export const DEFAULT_HANDOVER_FORMAT_TEMPLATE_KEY = 'CF-ETSI-102232';

/**
 * Maps the handover format an e-warrant asks for onto the collection function
 * template that can deliver it. Every ETSI TS 102 232 part is served by the same
 * collection function type.
 */
export const HANDOVER_FORMAT_TEMPLATE_KEYS: { [handoverFormat: string]: string } = {
  'TS102232-2': DEFAULT_HANDOVER_FORMAT_TEMPLATE_KEY,
  'TS102232-3': DEFAULT_HANDOVER_FORMAT_TEMPLATE_KEY,
  'TS102232-4': DEFAULT_HANDOVER_FORMAT_TEMPLATE_KEY,
  'TS102232-5': DEFAULT_HANDOVER_FORMAT_TEMPLATE_KEY,
  'TS102232-6': DEFAULT_HANDOVER_FORMAT_TEMPLATE_KEY,
  'TS102232-7': DEFAULT_HANDOVER_FORMAT_TEMPLATE_KEY,
};

/**
 * Everything the collection function templates need to be resolved into a usable
 * form. These all come straight from the store — see CollectionFunctionCreate, which
 * feeds the same values into the same adapters.
 */
export type CollectionFunctionTemplateContext = {
  collectionFunctionTemplates: { [key: string]: FormTemplate }
  timezones: ValueLabelPair[]
  contacts: Contact[]
  collectionFunctionGroupList: CollectionFunctionGroup[]
  cfFtpInfos: CfFtpInfo[]
  networks: Network[]
  securityInfos: SecurityInfo[]
  fqdnGroups: FqdnGroup[]
  enabledCollectionFunctions: SupportedFeaturesCollectionFunctions
  xcipioNodes: NodeData[]
  countries: string[]
  is5GCVcpEnabled: boolean
  isLocationRequired: boolean
  isT1678HITlsEnabled: boolean
  is33108HITlsEnabled: boolean
};

/**
 * The distinct collection function template keys an e-warrant needs, derived from the
 * handover format of each of its service specific information blocks.
 *
 * A service that names no handover format takes the default template — the backend
 * defaults the same way, so the template it needs to create that collection function
 * has to travel with the request. A service that names a format with no mapping is
 * skipped instead: the backend reports that as a validation problem, which it can only
 * do if we do not quietly hand it a template of our own choosing.
 */
export function getCollectionFunctionTemplateKeys(eWarrant: EWarrant): string[] {
  const templateKeys = (eWarrant.services ?? [])
    .map(({ handoverFormat }) => (handoverFormat
      ? HANDOVER_FORMAT_TEMPLATE_KEYS[handoverFormat]
      : DEFAULT_HANDOVER_FORMAT_TEMPLATE_KEY))
    .filter((templateKey) => templateKey != null);

  return Array.from(new Set(templateKeys));
}

/**
 * Resolves a collection function template and fills every field with its template
 * default — the same pipeline CollectionFunctionCreate runs before its save. Only the
 * collection function itself is returned: the audit envelope getCollectionFunctionDataToSubmit
 * wraps it in is meaningful to the collection function create endpoint, not here.
 */
function buildCollectionFunction(
  template: FormTemplate,
  context: CollectionFunctionTemplateContext,
): CollectionFunction {
  let adaptedTemplate = adaptTemplateVisibilityMode(template, 'create');
  adaptedTemplate = adaptTemplate5GFields(adaptedTemplate, context.is5GCVcpEnabled);
  adaptedTemplate = adaptTemplateSecinfoidFields(
    adaptedTemplate,
    context.isT1678HITlsEnabled,
    context.is33108HITlsEnabled,
  );

  // forceInterfaceCreation, so the collection function carries an HI3 interface at its
  // template defaults even though the template declares it optional. The backend fills
  // that interface's destination from the service's ccDeliveryEndpoint, and removes the
  // interface altogether when the service delivers no CC — neither of which it could do
  // from a template it does not have.
  const formData = createCollectionFunctionBasedOnTemplate(adaptedTemplate, true) as CollectionFunctionFormData;

  return getCollectionFunctionDataToSubmit(formData, null, 'CREATE').data;
}

/**
 * Builds one collection function — every field carrying its template default — for
 * each distinct handover format the e-warrant asks for. Returns an empty array when
 * no handover format maps to an available template.
 */
export default function buildCollectionFunctionsFromEWarrant(
  eWarrant: EWarrant,
  context: CollectionFunctionTemplateContext,
): CollectionFunction[] {
  const templateKeys = getCollectionFunctionTemplateKeys(eWarrant);

  if (templateKeys.length === 0) {
    return [];
  }

  let availableTemplates = adaptTemplateOptions(
    context.collectionFunctionTemplates,
    context.timezones,
    context.contacts,
    context.collectionFunctionGroupList,
    context.cfFtpInfos,
    context.networks,
    context.securityInfos,
    context.fqdnGroups,
    context.enabledCollectionFunctions,
    context.xcipioNodes,
    context.countries,
  );

  availableTemplates = adaptTemplatesBasedOnAppSettings(
    availableTemplates,
    context.isLocationRequired,
    context.isT1678HITlsEnabled,
    context.is33108HITlsEnabled,
  );

  return templateKeys
    .filter((templateKey) => availableTemplates[templateKey] != null)
    .map((templateKey) => buildCollectionFunction(availableTemplates[templateKey], context));
}
